import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { useSystem } from '@ohif/core';
import ToggleSwitch from '../common/ToggleSwitch';
import { ICON_STROKE_WIDTH } from '../../constants';
import { ESCALATION_CONTACT_ROLES, getRoleMeta } from '../../constants/escalationContact.constants';
import {
  listEscalationContacts,
  patchEscalationContactStatus,
} from '../../services/escalationContactService';
import type { EscalationContact } from '../../types/escalationContact.types';
import { formatMobileDisplay } from '../../utils/escalationContactValidation';

export interface EscalationContactsSectionHandle {
  reload: () => void;
}

interface EscalationContactsSectionProps {
  facilityId: number;
  onAddContact: () => void;
  onEditContact: (contact: EscalationContact) => void;
  onContactsUpdated?: () => void;
}

const EscalationContactsSection = forwardRef<
  EscalationContactsSectionHandle,
  EscalationContactsSectionProps
>(function EscalationContactsSection(
  { facilityId, onAddContact, onEditContact, onContactsUpdated },
  ref
) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [contacts, setContacts] = useState<EscalationContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await listEscalationContacts({
      facilityId,
      role: roleFilter !== 'all' ? roleFilter : undefined,
    });
    if (res.success) {
      setContacts(res.data);
    } else {
      setContacts([]);
      setError(res.error || 'Failed to load escalation contacts');
    }
    setLoading(false);
  }, [facilityId, roleFilter]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useImperativeHandle(ref, () => ({ reload: loadContacts }), [loadContacts]);

  const handleToggleActive = async (contact: EscalationContact, checked: boolean) => {
    const nextStatus = checked ? 'Active' : 'Inactive';
    setTogglingId(contact.id);
    const res = await patchEscalationContactStatus(contact.id, facilityId, nextStatus);
    setTogglingId(null);

    if (res.success && res.data) {
      setContacts(prev => prev.map(c => (c.id === contact.id ? res.data! : c)));
      onContactsUpdated?.();
      uiNotificationService?.show({
        title: 'Status updated',
        message: `${contact.name} is now ${nextStatus.toLowerCase()}.`,
        type: 'success',
        duration: 2500,
      });
      return;
    }

    uiNotificationService?.show({
      title: 'Status update failed',
      message: res.error || 'Unable to update contact status',
      type: 'error',
      duration: 4000,
    });
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <p className="esc-step-label">STEP 1</p>
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Escalation contacts</h2>
          <span className="esc-count-badge">{contacts.length}</span>
        </div>
        <p className="esc-step-subtitle">
          Maintain who can receive critical-result alerts, grouped by clinical role.
        </p>
        <p className="esc-step-helper">
          People who receive critical-result alerts. Each role can have multiple contacts; only
          active contacts are selectable in the matrix.
        </p>
      </header>

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <div className="esc-select-wrap esc-filter-select-wrap">
            <select
              className="esc-filter-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              aria-label="Filter by role"
            >
              <option value="all">All roles</option>
              {ESCALATION_CONTACT_ROLES.map(role => (
                <option
                  key={role.value}
                  value={role.value}
                >
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={onAddContact}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add contact
          </button>
        </div>
      </div>

      {error && (
        <p
          className="esc-error-banner"
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Department</th>
              <th>Mobile</th>
              <th>Alternate</th>
              <th>Active</th>
              <th
                className="esc-table-actions-col"
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  Loading contacts…
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  No escalation contacts found. Add a contact to get started.
                </td>
              </tr>
            ) : (
              contacts.map(contact => {
                const roleMeta = getRoleMeta(contact.role);
                const isInactive = contact.status !== 'Active';
                return (
                  <tr
                    key={contact.id}
                    className={[
                      'esc-table-row--clickable',
                      isInactive ? 'esc-table-row--inactive' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => onEditContact(contact)}
                  >
                    <td className="esc-table-name">{contact.name}</td>
                    <td>
                      <span className={`esc-role-badge ${roleMeta.badgeClass}`}>
                        {roleMeta.label}
                      </span>
                    </td>
                    <td className="esc-table-muted">{contact.department?.trim() || '—'}</td>
                    <td className="esc-table-mobile">
                      {formatMobileDisplay(contact.mobileNumber)}
                    </td>
                    <td className="esc-table-mobile esc-table-mobile--muted">
                      {formatMobileDisplay(contact.alternateMobileNumber)}
                    </td>
                    <td
                      className="esc-table-active-col"
                      onClick={e => e.stopPropagation()}
                    >
                      <ToggleSwitch
                        checked={contact.status === 'Active'}
                        disabled={togglingId === contact.id}
                        onChange={checked => handleToggleActive(contact, checked)}
                      />
                    </td>
                    <td className="esc-table-actions-col">
                      <button
                        type="button"
                        className="esc-icon-btn"
                        aria-label={`Edit ${contact.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          onEditContact(contact);
                        }}
                      >
                        <Pencil
                          size={16}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
});

export default EscalationContactsSection;
